/**
 * Feedback (CHAP-262) - Postgres-backed port of omega-club CHAP-260.
 * Submit (persist + email Jake) + Cabinet-visible list with email-status pills.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantData } from '../hooks/useTenantData.js';
import { useUserRoles } from '../hooks/useUserRoles.js';
import { useFeedbackList, useSubmitFeedback, useDeleteFeedback } from '../hooks/useFeedback.js';
import type { FeedbackEmailStatus } from '../../shared/types.js';

const PILL: Record<FeedbackEmailStatus, { label: string; cls: string }> = {
  sent: { label: 'Emailed', cls: 'bg-green-100 text-green-800' },
  pending: { label: 'Saved', cls: 'bg-gray-100 text-gray-700' },
  no_api_key: { label: 'Saved (no email)', cls: 'bg-amber-100 text-amber-800' },
  failed: { label: 'Email failed', cls: 'bg-red-100 text-red-800' },
};

export default function Feedback() {
  const { slug = '' } = useParams();
  const { data } = useTenantData(slug);
  const { user } = useUserRoles();
  const authed = !!user;

  const list = useFeedbackList(slug, authed);
  const submit = useSubmitFeedback(slug);
  const del = useDeleteFeedback(slug);

  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [banner, setBanner] = useState<FeedbackEmailStatus | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    const r = await submit.mutateAsync({
      text: text.trim(),
      name: authed ? undefined : name.trim() || undefined,
      email: authed ? undefined : email.trim() || undefined,
      page: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
    setBanner((r.emailStatus as FeedbackEmailStatus) ?? 'pending');
    setText('');
  }

  return (
    <div className="space-y-4 min-w-0 max-w-3xl">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h2 className="text-navy text-xl sm:text-2xl font-bold">Feedback</h2>
        <p className="text-neutral text-sm mt-1">
          Share thoughts on the {data?.tenant.displayName ?? 'chapter'} dashboard. Submissions go to{' '}
          <a className="text-blue-600 underline" href="mailto:jake@1116.info">jake@1116.info</a>.
        </p>
      </header>

      {banner && (
        <div
          className={`rounded-xl p-3 text-sm border ${
            banner === 'sent'
              ? 'border-green-200 bg-green-50 text-green-800'
              : banner === 'failed'
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          {banner === 'sent'
            ? 'Sent - thanks! Your feedback was saved and emailed.'
            : banner === 'failed'
              ? 'Saved, but the email failed to send. Your feedback is recorded.'
              : 'Saved - thanks! (Email delivery is not configured in this environment.)'}
        </div>
      )}

      <form onSubmit={onSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-3 min-w-0">
        {!authed && (
          <div className="grid sm:grid-cols-2 gap-3 min-w-0">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
        )}
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={10_000} placeholder="What's working, what's not, what would help…" className="w-full border border-gray-300 rounded px-3 py-2 text-sm min-w-0" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-neutral">{text.length}/10000</span>
          <button type="submit" disabled={submit.isPending || !text.trim()} className="px-4 py-2 rounded bg-navy text-white text-sm font-medium disabled:opacity-50">
            {submit.isPending ? 'Sending…' : 'Send feedback'}
          </button>
        </div>
        {submit.isError && <p className="text-xs text-red-600">{submit.error.message}</p>}
      </form>

      {/* List - authed only */}
      {!authed ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 text-sm text-neutral">
          Sign in to see submitted feedback.
        </div>
      ) : (
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
          <h3 className="text-navy text-sm font-semibold mb-3">
            {list.data?.canSeeAll ? 'All feedback' : 'Your feedback'}
          </h3>
          {list.isLoading && <p className="text-xs text-neutral">Loading…</p>}
          {list.data && list.data.feedback.length === 0 && <p className="text-xs text-neutral">No feedback yet.</p>}
          <ul className="space-y-3">
            {list.data?.feedback.map((f) => {
              const pill = PILL[f.emailStatus];
              const canDelete = list.data?.canSeeAll || f.submitterUserId === user?.id;
              return (
                <li key={f.id} className="border border-gray-100 rounded-lg p-3 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-sm font-medium text-navy truncate">{f.submitterName}</span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${pill.cls}`}>{pill.label}</span>
                      <span className="text-xs text-neutral">{new Date(f.createdAt).toLocaleDateString()}</span>
                      {canDelete && (
                        <button onClick={() => del.mutate(f.id)} disabled={del.isPending} className="text-xs text-red-600 hover:underline disabled:opacity-50">
                          Delete
                        </button>
                      )}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap break-words">{f.text}</p>
                  {f.page && <p className="text-[11px] text-neutral mt-1">on {f.page}</p>}
                </li>
              );
            })}
          </ul>
          <p className="text-[11px] text-neutral mt-4">
            Entries are stored durably in ChapterBuild; email delivery to Jake is best-effort and shown per row.
          </p>
        </section>
      )}
    </div>
  );
}
